import { createMuiTheme, Theme } from '@material-ui/core/styles'

import { COLORS } from './colors'
import { FONT_FAMILY, FONT_WEIGHT } from './fonts'

interface BackofficeThemeOptions {
  secondaryMain?: string
  background?: string
  primaryContrast?: string
}

const createBackofficeTheme = ({
  secondaryMain = COLORS.SECONDARY_MAIN,
  background = COLORS.PRIMARY_BACKGROUND,
  primaryContrast = COLORS.WHITE,
}: BackofficeThemeOptions = {}): Theme =>
  createMuiTheme({
    typography: {
      fontFamily: FONT_FAMILY,
      fontWeightLight: FONT_WEIGHT.regular,
      fontWeightRegular: FONT_WEIGHT.regular,
      fontWeightMedium: FONT_WEIGHT.semiBold,
      fontWeightBold: FONT_WEIGHT.bold,
      h1: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.bold,
        color: COLORS.FONT,
      },
      h2: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.bold,
        color: COLORS.FONT,
      },
      h3: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.semiBold,
        color: COLORS.FONT,
      },
      h4: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.semiBold,
        color: COLORS.FONT,
      },
      h5: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.semiBold,
        color: COLORS.PRIMARY,
      },
      h6: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.semiBold,
        color: COLORS.PRIMARY,
      },
      subtitle1: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.semiBold,
        color: COLORS.FONT,
      },
      subtitle2: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.medium,
        color: COLORS.FONT,
      },
      body1: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.regular,
        color: COLORS.FONT,
      },
      body2: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.regular,
        color: COLORS.FONT_SECONDARY,
      },
      button: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.semiBold,
        textTransform: 'none',
      },
      caption: {
        fontFamily: FONT_FAMILY,
        fontWeight: FONT_WEIGHT.regular,
      },
    },
    palette: {
      primary: {
        main: COLORS.PRIMARY,
        contrastText: primaryContrast,
      },
      secondary: {
        main: secondaryMain,
      },
      background: {
        default: background,
      },
      text: {
        primary: COLORS.FONT,
        secondary: COLORS.FONT_SECONDARY,
      },
    },
    props: {
      MuiCheckbox: {
        color: 'primary',
      },
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          body: {
            fontFamily: FONT_FAMILY,
            color: COLORS.FONT,
          },
        },
      },
      MuiButton: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.semiBold,
          textTransform: 'none',
          borderRadius: 0,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: COLORS.BUTTON,
          color: COLORS.WHITE,
          '&:hover': {
            backgroundColor: COLORS.ACCENT,
          },
        },
        outlined: {
          backgroundColor: COLORS.WHITE,
          color: COLORS.BUTTON,
          border: `1px solid ${COLORS.BUTTON}`,
          '&:hover': {
            backgroundColor: COLORS.WHITE,
            border: `1px solid ${COLORS.BUTTON}`,
          },
        },
        outlinedPrimary: {
          backgroundColor: COLORS.WHITE,
          color: COLORS.BUTTON,
          border: `1px solid ${COLORS.BUTTON}`,
          '&:hover': {
            backgroundColor: COLORS.WHITE,
            border: `1px solid ${COLORS.BUTTON}`,
          },
        },
        text: {
          fontWeight: FONT_WEIGHT.medium,
        },
      },
      MuiTab: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.regular,
          textTransform: 'none',
        },
        textColorPrimary: {
          color: COLORS.PRIMARY,
          opacity: 1,
          '&$selected': {
            fontWeight: FONT_WEIGHT.semiBold,
            color: COLORS.PRIMARY,
          },
        },
        textColorInherit: {
          color: COLORS.PRIMARY,
          opacity: 1,
          '&$selected': {
            fontWeight: FONT_WEIGHT.semiBold,
            color: COLORS.PRIMARY,
          },
        },
      },
      MuiTabs: {
        indicator: {
          backgroundColor: COLORS.PRIMARY,
        },
      },
      MuiTableCell: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.regular,
        },
        head: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.semiBold,
          color: COLORS.FONT,
        },
      },
      MuiInputBase: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.regular,
        },
      },
      MuiInput: {
        underline: {
          '&:after': {
            borderBottomColor: COLORS.BUTTON,
          },
          '&$error:after': {
            borderBottomColor: COLORS.BUTTON,
          },
          '&$error:before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.42)',
          },
          '&$error:hover:not($disabled):before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          '&$focused $notchedOutline': {
            borderColor: COLORS.BUTTON,
          },
          '&$error $notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&$error:hover $notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
          },
          '&$error$focused $notchedOutline': {
            borderColor: COLORS.BUTTON,
          },
        },
      },
      MuiFormLabel: {
        root: {
          '&$focused': {
            color: COLORS.BUTTON,
          },
          '&$error': {
            color: '#f44336',
          },
          '&$error$focused': {
            color: '#f44336',
          },
        },
      },
      MuiInputLabel: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.regular,
          '&$focused': {
            color: COLORS.BUTTON,
          },
          '&$error': {
            color: '#f44336',
          },
          '&$error$focused': {
            color: '#f44336',
          },
        },
      },
      MuiFormHelperText: {
        root: {
          fontFamily: FONT_FAMILY,
          '&$error': {
            color: COLORS.FONT_SECONDARY,
          },
        },
      },
      MuiTooltip: {
        tooltip: {
          fontFamily: FONT_FAMILY,
          fontSize: 12,
          fontWeight: FONT_WEIGHT.regular,
        },
      },
      MuiListItemText: {
        primary: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.medium,
        },
      },
      MuiChip: {
        root: {
          fontFamily: FONT_FAMILY,
          fontWeight: FONT_WEIGHT.semiBold,
        },
        colorPrimary: {
          backgroundColor: COLORS.PRIMARY,
          color: COLORS.WHITE,
          '& .MuiChip-deleteIcon': {
            color: COLORS.WHITE,
          },
        },
        deleteIconColorPrimary: {
          color: COLORS.WHITE,
          '&:hover, &:active': {
            color: COLORS.WHITE,
          },
        },
      },
      MuiDialogTitle: {
        root: {
          fontFamily: FONT_FAMILY,
        },
      },
      MuiIconButton: {
        root: {
          color: COLORS.FONT,
        },
      },
      MuiCheckbox: {
        root: {
          '&&': {
            color: COLORS.BUTTON,
            '&$checked': {
              color: COLORS.PRIMARY,
            },
          },
        },
        colorPrimary: {
          color: COLORS.BUTTON,
          '&$checked': {
            color: COLORS.PRIMARY,
          },
        },
        colorSecondary: {
          color: COLORS.BUTTON,
          '&$checked': {
            color: COLORS.PRIMARY,
          },
        },
      },
      MuiSwitch: {
        colorPrimary: {
          '&$checked': {
            color: COLORS.SUCCESS,
          },
          '&$checked + $track': {
            backgroundColor: COLORS.SUCCESS,
          },
        },
      },
    },
  })

export { createBackofficeTheme }
